import React, { useState, useCallback } from 'react';
import { debounce } from 'lodash';
import emailSpellChecker from '@zootools/email-spell-checker';
import { Field, useField, useFormikContext } from 'formik';
import { Input } from '@dashboard/components/ui/input';
import { cn } from '@dashboard/lib/utils';
import ClearFieldButton from './ClearFieldButton';

const EmailField = ({
  name,
  className,
  inputClassName,
  clearable,
  // Engine-only props: Form.js renders these itself and still spreads them
  // down, so they reach the DOM as unknown attributes unless dropped here.
  helpText: _helpText,
  optional: _optional,
  visible: _visible,
  tooltip: _tooltip,
  additionalContent: _additionalContent,
  ...props
}) => {
  const [field] = useField(name);
  const { setFieldValue } = useFormikContext();
  const [suggestion, setSuggestion] = useState(null);

  const checkSpelling = useCallback(
    debounce(value => {
      if (!value) {
        setSuggestion(null);
        return;
      }
      const result = emailSpellChecker.run({ email: value });
      setSuggestion(result?.full ?? null);
    }, 300),
    [],
  );

  const handleChange = e => {
    field.onChange(e);
    checkSpelling(e.target.value);
  };

  const acceptSuggestion = () => {
    if (!suggestion) return;
    setFieldValue(name, suggestion);
    setSuggestion(null);
  };

  return (
    <div>
      <div className={cn(clearable && 'group/clear relative')}>
        <Field
          {...props}
          name={name}
          type="email"
          as={Input}
          value={field.value ?? ''}
          className={cn(className, inputClassName, clearable && 'pr-9')}
          onChange={handleChange}
        />
        {clearable && (
          <ClearFieldButton
            name={name}
            disabled={props.disabled}
            onClear={() => setSuggestion(null)}
            className="inset-y-0 my-auto h-fit"
          />
        )}
      </div>
      {suggestion && (
        <button
          type="button"
          onClick={acceptSuggestion}
          className="mt-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Did you mean <span className="underline">{suggestion}</span>?
        </button>
      )}
    </div>
  );
};

export { EmailField };
export default EmailField;
